import type { BusSeatFloorView, BusSeatView, BusSegmentPick } from '../models.js';

/** Uma faixa HORIZONTAL do ônibus desenhado de lado (a frente à esquerda, como
 *  no frame): uma coluna real do bus-api, com uma célula por fileira. `null` é
 *  um vão (a fileira não tem poltrona naquela coluna). */
type BusSeatLane = {
  readonly column: number;
  readonly cells: readonly (BusSeatView | null)[];
};

/** O deque desenhado: as faixas de um lado do corredor, o corredor, as do
 *  outro. `aisleAfter` é quantas faixas vêm antes do corredor. `estimated`:
 *  o bus-api não mandou coluna nenhuma e as faixas são um PALPITE (2+2 pela
 *  ordem do número) — a planta fica de pé, mas janela, corredor e vizinho não
 *  são afirmados. */
export type BusSeatDeck = {
  readonly lanes: readonly BusSeatLane[];
  readonly aisleAfter: number;
  readonly estimated: boolean;
};

/** As colunas do palpite sem coluna: o 2+2, o ônibus mais comum. */
const ESTIMATED_COLUMNS = [1, 2, 4, 5] as const;

const byCode = (a: BusSeatView, b: BusSeatView) =>
  a.code.localeCompare(b.code, undefined, { numeric: true });

/** A coluna de cada poltrona da fileira: a informada, ou — sem ela — a menor
 *  coluna ainda livre NA fileira. Assim uma poltrona sem coluna, ao lado de uma
 *  numerada, nunca some da planta. */
function placedSeats(
  row: BusSeatFloorView['rows'][number],
): { column: number; seat: BusSeatView }[] {
  const used = new Set(
    row.seats.flatMap((seat) => (seat.column === undefined ? [] : [seat.column])),
  );
  let next = 1;
  return row.seats.map((seat) => {
    if (seat.column !== undefined) return { column: seat.column, seat };
    while (used.has(next)) next += 1;
    used.add(next);
    return { column: next, seat };
  });
}

/**
 * O andar como PLANTA horizontal: cada fileira (`rowNumber`, crescente da
 * frente para o fundo) vira uma coluna de células, e cada coluna real (1, 2 |
 * 4, 5) vira uma faixa. O corredor fica entre a coluna 2 e a 4 — o 2+1 do
 * leito-cama cai das colunas presentes. Sem nenhuma coluna informada, as
 * poltronas enchem um 2+2 pela ordem do número, quatro por fileira, e o deque
 * sai `estimated`.
 */
export function busSeatDeck(floor: BusSeatFloorView): BusSeatDeck {
  const rows = [...floor.rows].sort((a, b) => a.rowNumber - b.rowNumber);
  const seats = rows.flatMap((row) => row.seats);
  if (!seats.some((seat) => seat.column !== undefined)) {
    const sorted = [...seats].sort(byCode);
    const perRow = ESTIMATED_COLUMNS.length;
    const count = Math.ceil(sorted.length / perRow);
    const lanes = ESTIMATED_COLUMNS.map((column, index) => ({
      column,
      cells: Array.from({ length: count }, (_, row) => sorted[row * perRow + index] ?? null),
    }));
    return { lanes, aisleAfter: 2, estimated: true };
  }
  const placed = rows.map(placedSeats);
  const columns = [...new Set(placed.flatMap((row) => row.map(({ column }) => column)))].sort(
    (a, b) => a - b,
  );
  const lanes = columns.map((column) => ({
    column,
    cells: placed.map((row) => row.find((cell) => cell.column === column)?.seat ?? null),
  }));
  return { lanes, aisleAfter: columns.filter((column) => column <= 2).length, estimated: false };
}

/** O terço do ônibus: frente, meio ou fundo. */
export type BusSeatArea = 'front' | 'middle' | 'back';

/** O que a planta sabe de uma poltrona pelo lugar dela no deque — o bus-api não
 *  manda nada disso, tudo sai da coluna e da fileira.
 *  - `window`: a faixa da ponta; `aisle`: a faixa colada no corredor (no 2+1 do
 *    leito-cama, a faixa sozinha de um lado é as duas coisas);
 *  - `individual`: o único assento do seu lado da fileira (o 2+1);
 *  - `area`: o terço das fileiras do andar;
 *  - `neighbor`: a poltrona ao lado, do mesmo lado do corredor — `null` quando
 *    não há (o individual, um vão);
 *  - `row`, `lane`, `side`: a fileira, a faixa e o lado do corredor (0 = antes).
 *  No deque `estimated`, janela, corredor, individual e vizinho ficam
 *  desligados; o terço fica, porque o número cresce da frente para o fundo. */
export type BusSeatTraits = {
  readonly window: boolean;
  readonly aisle: boolean;
  readonly individual: boolean;
  readonly area: BusSeatArea;
  readonly neighbor: 'free' | 'occupied' | null;
  readonly row: number;
  readonly lane: number;
  readonly side: 0 | 1;
};

/** O terço de uma fileira (0 = a da frente) num andar de `rows` fileiras. */
export function busSeatArea(row: number, rows: number): BusSeatArea {
  const third = rows / 3;
  if (row < third) return 'front';
  return row < 2 * third ? 'middle' : 'back';
}

/** O vizinho: livre se alguma poltrona ao lado está livre, ocupado se todas
 *  estão ocupadas, `null` sem poltrona ao lado. */
function neighborOf(cells: readonly (BusSeatView | null | undefined)[]): BusSeatTraits['neighbor'] {
  const seats = cells.filter((cell): cell is BusSeatView => Boolean(cell));
  if (seats.length === 0) return null;
  return seats.some((seat) => !seat.occupied) ? 'free' : 'occupied';
}

/** Os traços de cada poltrona do deque, pelo código. */
export function busSeatTraits(deck: BusSeatDeck): ReadonlyMap<string, BusSeatTraits> {
  const { lanes, aisleAfter, estimated } = deck;
  const known = !estimated && lanes.length > 1;
  const hasAisle = aisleAfter > 0 && aisleAfter < lanes.length;
  const rows = Math.max(0, ...lanes.map((lane) => lane.cells.length));
  const sideOf = (lane: number): 0 | 1 => (lane < aisleAfter ? 0 : 1);
  const traits = new Map<string, BusSeatTraits>();
  lanes.forEach((laneView, lane) => {
    const side = sideOf(lane);
    const sideLanes = lanes.flatMap((_, other) => (sideOf(other) === side ? [other] : []));
    laneView.cells.forEach((seat, row) => {
      if (!seat) return;
      const beside = sideLanes
        .filter((other) => other !== lane)
        .map((other) => lanes[other]?.cells[row]);
      traits.set(seat.code, {
        window: known && (lane === 0 || lane === lanes.length - 1),
        aisle: known && hasAisle && (lane === aisleAfter - 1 || lane === aisleAfter),
        individual: known && sideLanes.length === 1,
        area: busSeatArea(row, rows),
        neighbor: known ? neighborOf(beside) : null,
        row,
        lane,
        side,
      });
    });
  });
  return traits;
}

/**
 * Tocar numa poltrona de um trecho — as regras do pacote Flutter de ônibus:
 * - ocupada não faz nada;
 * - tocar numa já escolhida a DESMARCA (o viajante dela fica sem poltrona);
 * - com UM viajante, tocar noutra TROCA a poltrona dele;
 * - com vários, a poltrona vai para o primeiro viajante ainda sem poltrona; com
 *   todos servidos, o toque não faz nada (desmarque uma antes).
 * Partir de "seguir sem assento" e tocar numa poltrona volta à escolha.
 */
export function toggleBusSeat(
  pick: BusSegmentPick | undefined,
  travelerIds: readonly string[],
  seat: BusSeatView,
): BusSegmentPick | undefined {
  if (seat.occupied) return pick;
  const seats = pick?.kind === 'seats' ? pick.seats : {};
  const holder = Object.keys(seats).find((id) => seats[id] === seat.code);
  if (holder) {
    return {
      kind: 'seats',
      seats: Object.fromEntries(Object.entries(seats).filter(([id]) => id !== holder)),
    };
  }
  const [only] = travelerIds;
  if (travelerIds.length === 1 && only !== undefined) {
    return { kind: 'seats', seats: { [only]: seat.code } };
  }
  const next = travelerIds.find((id) => !Object.hasOwn(seats, id));
  return next === undefined ? pick : { kind: 'seats', seats: { ...seats, [next]: seat.code } };
}

/** O trecho está resolvido: sem assento, ou uma poltrona para cada viajante. */
export function busSegmentComplete(
  pick: BusSegmentPick | undefined,
  travelerIds: readonly string[],
): boolean {
  if (!pick) return false;
  if (pick.kind === 'noSeat') return true;
  return travelerIds.every((id) => Object.hasOwn(pick.seats, id));
}

/** O trecho não acomoda o GRUPO: menos poltronas livres que viajantes (o
 *  lotado é o caso de zero). A saída é "seguir sem assento" — com uma poltrona
 *  para dois, o segundo viajante não teria onde sentar. */
export function busSeatsShort(floors: readonly BusSeatFloorView[], travelers: number): boolean {
  const free = floors
    .flatMap((floor) => floor.rows)
    .flatMap((row) => row.seats)
    .filter((seat) => !seat.occupied).length;
  return free < Math.max(travelers, 1);
}
